import { COSIGN_TOKEN_KEY, COSIGN_X_PLATFORM_TOKEN } from '@constants'
import { datadogRum } from '@datadog/browser-rum'
import { useDialog } from '@dni-hooks/useDialog'
import { getCrossBrandMallCampaignsFetcher } from '@hooks-api/fortune/use-api-cross-brand-mall-campaigns'
import { getCampaignMemberFetcher } from '@hooks-api/fortune/useApiCampaignMember'
import { useApiTokenMultiLogin } from '@hooks-api/fortune/useApiTokenMultiLogin'
import { Button } from '@pets-ui/Button/Button'
import { Text } from '@pets-ui/Text/Text'
import { VStack } from '@pets-ui/VStack/VStack'
import { type AxiosError } from 'axios'
import Cookies from 'js-cookie'
import queryString from 'query-string'
import { useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom-v5'
import UnauthorizedErrorIcon from '../assets/unauthorized-error.svg?react'
import { LoaderStm } from '../components/LoaderStm'
import { STM_ALLOWED_ROLES, STM_COUNTRY_CONFIG, USER_ROLE } from '../constants'
import { BrandCategory, type BrandInfo } from '../constants/brands'
import { STM_BRAND_CATEGORY } from '../constants/stm-brand-category'
import { useCountryCode, useStmBrandStore } from '../hooks'
import { clearStmCookies } from '../utils'
import { type StmAuthErrorKind, STM_AUTH_ERROR_KIND, STM_AUTH_ERROR_MESSAGE } from './auth-error'
import { DialogOtpUnverified, DIALOG_OTP_UNVERIFIED } from './DialogOtpUnverified'

interface StmAuthProcessingProps {
  basePath: string
}

async function fetchMallCampaignsSafely(mallCode: string) {
  try {
    const fetchMallCampaigns = getCrossBrandMallCampaignsFetcher()
    return await fetchMallCampaigns(`/cross-brand-malls/${mallCode}/campaigns`)
  }
  catch (error) {
    console.error('[StmAuthProcessing] failed to fetch mall campaigns', error)
    return null
  }
}

export const StmAuthProcessing = ({ basePath }: StmAuthProcessingProps) => {
  const { search } = useLocation()
  const history = useHistory()
  const countryCode = useCountryCode()
  const { setBrands, setBrand, setCountryCode, setUserRole } = useStmBrandStore()
  const [errorKind, setErrorKind] = useState<StmAuthErrorKind | null>(null)
  const { openDialog: openOtpDialog } = useDialog(DIALOG_OTP_UNVERIFIED)

  const countryConfig = STM_COUNTRY_CONFIG[countryCode]
  const defaultCampaignCode = countryConfig?.defaultCampaignCode
  const mallCode = countryConfig?.mallCode
  const queryParams = queryString.parse(search)
  const token = typeof queryParams.token === 'string' ? queryParams.token : undefined

  const { isLoading } = useApiTokenMultiLogin(
    defaultCampaignCode && token
      ? { campaign_code: defaultCampaignCode, cosign_token: token }
      : undefined,
    {
      onSuccess: async ({ data }) => {
        const credentialsByProject = data.data

        if (!mallCode) {
          setErrorKind(STM_AUTH_ERROR_KIND.DEFAULT)
          return
        }

        // 拉取該 mall 內可用 campaign 列表
        const campaignsResponse = await fetchMallCampaignsSafely(mallCode)
        if (!campaignsResponse) {
          setErrorKind(STM_AUTH_ERROR_KIND.MALL_CAMPAIGNS_UNAVAILABLE)
          return
        }
        const campaigns = campaignsResponse.data.data

        if (campaigns.length === 0) {
          setErrorKind(STM_AUTH_ERROR_KIND.NO_AVAILABLE_BRANDS)
          return
        }

        // 以 API 回傳的 campaigns 為主，過濾出後端有回傳的品牌
        const brandEntries = campaigns.flatMap((campaign) => {
          const credentials = credentialsByProject[campaign.project_code]
          return credentials ? [{ campaign, credentials }] : []
        })

        if (brandEntries.length === 0) {
          setErrorKind(STM_AUTH_ERROR_KIND.DEFAULT)
          return
        }

        // 檢查 otp_verified：任一品牌有驗證即通過
        const hasOtpVerified = brandEntries.some(({ credentials }) => credentials.otp_verified.length > 0)

        if (!hasOtpVerified) {
          openOtpDialog()
          return
        }

        // 寫 cookie（先全部寫入，讓 interceptor 能帶 token 打 member API）
        brandEntries.forEach(({ campaign, credentials }) => {
          Cookies.set(`stm_${campaign.code}_${COSIGN_TOKEN_KEY}`, credentials.access_token)
          Cookies.set(`stm_${campaign.code}_${COSIGN_X_PLATFORM_TOKEN}`, credentials.x_platform_token)
        })

        // 逐品牌打 member API，檢查角色
        const fetchMember = getCampaignMemberFetcher()
        const memberResults = await Promise.allSettled(brandEntries.map(({ campaign }) =>
          fetchMember(`/campaigns/${campaign.code}/member`),
        ))

        const authorizedBrands = brandEntries.filter((_, index) => {
          const result = memberResults[index]
          if (result?.status !== 'fulfilled') return false
          const roles = result.value.data.data.roles
          return roles.some(role => (STM_ALLOWED_ROLES as readonly string[]).includes(role))
        })

        // 移除沒通過品牌的 cookie
        brandEntries
          .filter(entry => !authorizedBrands.includes(entry))
          .forEach(({ campaign }) => {
            Cookies.remove(`stm_${campaign.code}_${COSIGN_TOKEN_KEY}`)
            Cookies.remove(`stm_${campaign.code}_${COSIGN_X_PLATFORM_TOKEN}`)
          })

        if (authorizedBrands.length === 0) {
          setErrorKind(STM_AUTH_ERROR_KIND.DEFAULT)
          return
        }

        // 寫 store
        const authorizedBrandInfos: BrandInfo[] = authorizedBrands.map(({ campaign }) => ({
          campaignCode: campaign.code,
          campaignId: campaign.id,
          projectCode: campaign.project_code,
          countryCode,
          category: STM_BRAND_CATEGORY[campaign.project_code] ?? BrandCategory.PET,
        }))
        setBrands(authorizedBrandInfos)
        setCountryCode(countryCode)

        // 從第一個 authorized brand 的角色進行判斷，寫入 userRole。
        // 優先度 shop_manager > partner > null
        const firstAuthorizedBrand = authorizedBrands[0]
        if (firstAuthorizedBrand) {
          const firstAuthorizedIndex = brandEntries.indexOf(firstAuthorizedBrand)
          const firstAuthorizedMember = memberResults[firstAuthorizedIndex]
          const firstBrandRoles = firstAuthorizedMember?.status === 'fulfilled' ? firstAuthorizedMember.value.data.data.roles : []

          if (firstBrandRoles.includes(USER_ROLE.SHOP_MANAGER)) {
            setUserRole(USER_ROLE.SHOP_MANAGER)
          }
          else if (firstBrandRoles.includes(USER_ROLE.PARTNER)) {
            setUserRole(USER_ROLE.PARTNER)
          }
        }
        const defaultBrand = authorizedBrandInfos.find(brand => brand.campaignCode === defaultCampaignCode) ?? authorizedBrandInfos[0]
        if (defaultBrand) {
          setBrand(defaultBrand.campaignCode)
        }

        // 導向預設頁面
        history.push(basePath)
      },
      onError: () => {
        setErrorKind(STM_AUTH_ERROR_KIND.DEFAULT)
      },
      onErrorRetry: (error, _key, _config, revalidate, { retryCount }) => {
        if ((error as AxiosError).response?.status === 403) return
        if (retryCount >= 3) return
        revalidate({ retryCount })
      },
    },
  )

  const handleClickBack = () => {
    history.push(`${basePath}/login`)
  }

  const handleOtpLogout = () => {
    clearStmCookies()
    datadogRum.clearUser()
    handleClickBack()
  }

  if (isLoading) {
    return <LoaderStm />
  }

  if (errorKind) {
    return (
      <VStack gap={5} paddingTop="212px" alignItems="center" justifyContent="center">
        <UnauthorizedErrorIcon />
        <Text variant="h4">{STM_AUTH_ERROR_MESSAGE[errorKind]}</Text>
        <Button color="red" width="350px" onClick={handleClickBack}>回到登入頁</Button>
      </VStack>
    )
  }

  return (
    <>
      <LoaderStm />
      <DialogOtpUnverified onLogout={handleOtpLogout} />
    </>
  )
}
